import React, { useState, useEffect } from 'react';
import { useAccount, useReadContract, useWriteContract } from 'wagmi';
import { formatEther, zeroAddress } from 'viem';
import { MutableRefObject } from 'react';
import { Board, PaymentMethod } from '../utils/Primitives';
import { abi } from './PayoutMethodAbi';
import { useFetchSavePaymentMethods, getPaymentMethods } from './PaymentMethods';

interface PayoutViewV2Props {
    handleDidClose: () => void;
    boardRef: MutableRefObject<Board>;
    isModal?: boolean;
}

const PayoutMethodAddress = process.env.NEXT_PUBLIC_PUBLISHER_PAYOUT_CONTRACT_ADDRESS as `0x${string}` | null;

export default function PayoutViewV2({ handleDidClose, boardRef, isModal = false }: PayoutViewV2Props) {
    const { address } = useAccount();
    const [isLoading, setIsLoading] = useState(true);
    const [balance, setBalance] = useState<string>('0');
    const [selectedToken, setSelectedToken] = useState<PaymentMethod>({
        id: 0,
        address: zeroAddress,
        name: 'Ethereum',
        symbol: 'ETH',
        decimals: 18
    });

    // Fetch available payment methods
    const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useFetchSavePaymentMethods({ boardRef });
    const paymentMethodsList = Object.values(paymentMethods || {})
        .filter((method): method is PaymentMethod => method !== null)
        .slice(0, 25);

    // Read the user's balance from the contract
    const { data: balanceData, isError, isLoading: isBalanceLoading, refetch } = useReadContract({
        address: PayoutMethodAddress || undefined,
        abi,
        functionName: 'balanceOf',
        args: address ? [address, selectedToken.address] : undefined,
    });

    // Initialize the withdraw contract write operation
    const {
        writeContract,
        data: withdrawTxHash,
        isPending: isWithdrawLoading,
        isSuccess: isWithdrawSuccess,
        error: withdrawError
    } = useWriteContract();

    // Update the balance when data is fetched
    useEffect(() => {
        if (balanceData !== undefined) {
            const divisor = selectedToken.decimals ? Math.pow(10, selectedToken.decimals) : Math.pow(10, 18);
            const formattedBalance = (Number(balanceData) / divisor).toString();
            setBalance(formattedBalance);
            setIsLoading(false);
        } else if (!isBalanceLoading) {
            setIsLoading(false);
        }
    }, [balanceData, isBalanceLoading, selectedToken.decimals]);

    // Refresh the balance after a successful withdrawal
    useEffect(() => {
        if (isWithdrawSuccess) {
            setTimeout(() => {
                refetch();
            }, 2000);
        }
    }, [isWithdrawSuccess, refetch]);

    const handleWithdraw = async () => {
        if (!PayoutMethodAddress || !address) return;

        try {
            await writeContract({
                address: PayoutMethodAddress,
                abi,
                functionName: 'withdraw',
                args: [[selectedToken.address], address],
            });
        } catch (error) {
            console.error('Failed to withdraw:', error);
        }
    };

    const handleTokenChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = parseInt(event.target.value);
        const newToken = paymentMethodsList.find(method => method.id === selectedId);
        if (newToken) {
            setIsLoading(true);
            setSelectedToken(newToken);
        }
    };

    return (
        <div className={`relative flex flex-col items-center justify-center px-2 py-4 md:p-6 shadow-lg w-full ${isModal ? '' : 'min-h-screen'}`}>
            <div className="max-w-screen-xl w-full flex flex-col items-center justify-center">
                {!isModal && (
                    <div className='text-base md:text-xl md:leading-loose text-center px-0 mt-20 mb-0 md:mt-8 md:mb-2 '>
                        Your Payouts<br />
                    </div>
                )}

                {!address && (
                    <div className="mt-8 p-4 bg-gray-100 rounded-lg text-center">
                        <p>Connect your wallet to view your payouts.</p>
                    </div>
                )}

                {address && isLoading && (
                    <div className="mt-8 p-4 rounded-lg text-center">
                        <p>Loading your payouts...</p>
                    </div>
                )}

                {address && !isLoading && (
                    <div className="w-full mt-8">
                        <div className="bg-gray-100 p-4 rounded-lg mb-6">
                            <h2 className="text-lg font-bold mb-2">Available to Withdraw</h2>

                            {/* Token selector dropdown */}
                            <div className="mb-4">
                                <select
                                    value={selectedToken.id}
                                    onChange={handleTokenChange}
                                    className="w-full md:w-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={isLoadingPaymentMethods}
                                >
                                    {paymentMethodsList.map((method) => (
                                        <option key={method.id} value={method.id}>
                                            {method.name} ({method.symbol})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <div className="flex flex-col bg-white p-3 rounded-lg shadow-sm">
                                    <span className="text-lg font-semibold">
                                        {balance} {selectedToken.symbol}
                                    </span>
                                    <button
                                        onClick={handleWithdraw}
                                        disabled={isWithdrawLoading || parseFloat(balance) <= 0}
                                        className={`mt-2 px-3 py-1 rounded-full transition-colors text-sm ${
                                            parseFloat(balance) <= 0
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-blue-500 text-white hover:bg-blue-600 transform transition-transform duration-200 hover:scale-105'
                                        }`}
                                    >
                                        {isWithdrawLoading ? 'Processing...' : 'Withdraw'}
                                    </button>
                                </div>
                            </div>
                            {isWithdrawSuccess && (
                                <div className="mt-4 p-2 bg-green-100 text-green-700 rounded-md">
                                    Withdrawal successful! Transaction hash: {withdrawTxHash}
                                </div>
                            )}
                            {withdrawError && (
                                <div className="mt-4 p-2 bg-red-100 text-red-700 rounded-md">
                                    Error: {withdrawError.message || "Failed to withdraw"}
                                </div>
                            )}
                        </div>

                        <h2 className="text-lg font-bold mb-4">About Publisher Payouts</h2>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-gray-700 mb-2">
                                As a sticker publisher, you earn a share of the fees when your stickers are slapped.
                            </p>
                            <p className="text-gray-700">
                                Your earnings are automatically collected and can be withdrawn at any time.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {!isModal && (
                <div className='fixed top-2 right-2'>
                    <button
                        onClick={handleDidClose}
                        className="text-slate-800 m-4 py-1 px-3 text-2xl bg-slate-100 hover:bg-slate-300 rounded-full"
                    >
                        X
                    </button>
                </div>
            )}
        </div>
    );
}
